import axios from "axios";

// ── Instancia pública con X-Api-Key ────────────────────────────────────────
// Los endpoints de selectores y formularios de operadores usan API key,
// no JWT, ya que son accedidos desde dispositivos de planta sin sesión.
const apiPublic = axios.create({
  baseURL: process.env.VUE_APP_API,
  headers: {
    "Content-Type": "application/json",
    "X-Api-Key": process.env.VUE_APP_API_KEY,
  },
});

// ── Selectores (catálogos públicos para llenar el formulario) ───────────────

export const getMaquinasPublic = async () => {
  const response = await apiPublic.get("public/catalogo-maquinas/");
  return response.data;
};

export const getProductosPublic = async () => {
  const response = await apiPublic.get("public/catalogo-productos/");
  return response.data;
};

export const getPigmentosPublic = async () => {
  const response = await apiPublic.get("public/colores/");
  return response.data;
};

export const getOperadoresPublic = async () => {
  const response = await apiPublic.get("public/operadores/");
  return response.data;
};

export const getTPUsPublic = async () => {
  const response = await apiPublic.get("public/tpu/");
  return response.data;
};

// ── Envío del formulario ────────────────────────────────────────────────────

/**
 * Envía el formulario de Consumo de Materia Prima al back.
 * Recibe el payload ya construido por buildPayload() del composable.
 *
 * Payload esperado:
 * {
 *   tipo_reporte:   "materia_prima",
 *   fecha_registro: "2026-06-25T21:58:35.410Z",
 *   fecha:          "2026-06-25",
 *   hora:           "16:00",
 *   id_operador:    2,
 *   id_maquina:     1,
 *   id_producto:    9,
 *   id_color:       3,
 *   cantidad_pigmento: 100,
 *   aditivo:        true,
 *   tpu_items: [{ numero: 1, tpu_id: 2, cantidad: 10, unidad: "kg" }],
 *   molida:    { cantidad: 10, unidad: "kg" },
 *   comentarios: ""
 * }
 *
 * @param {object} payload — resultado de buildPayload()
 * @returns {object} — registro creado (ConsumoMateriaPrimaDetalleSerializer)
 */
export const crearConsumoMateriaPrima = async (payload) => {
  const response = await apiPublic.post(
    "formularios/consumo-materia-prima/",
    payload
  );
  return response.data;
};

// ── Exportar a Excel ────────────────────────────────────────────────────────
 
/**
 * Descarga el Excel de consumos de materia prima en el rango de fechas dado.
 * Requiere fecha_inicio y fecha_fin en formato YYYY-MM-DD.
 * Usa la instancia autenticada (JWT) porque es un endpoint del admin.
 *
 * @param {string} fechaInicio — YYYY-MM-DD
 * @param {string} fechaFin    — YYYY-MM-DD
 */
export const exportarConsumoMateriaPrima = async (fechaInicio, fechaFin) => {
  const { api } = await import("./api_backend");
 
  const response = await api.get("exportar/consumo-materia-prima/", {
    params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin },
    responseType: "blob",
  });
 
  const url  = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", `consumo_mp_${fechaInicio}_a_${fechaFin}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Plan vs Real por semana (gráfica de barras + % de cumplimiento).
 * Fuente: tabla PlanSemanal (sincronizada desde Excel).
 *
 * @param {object} params - { semana_inicio, semana_fin, anio? }
 * @returns {{ semanas: [{semana, label, plan, real, porcentaje}], total_plan, total_real }}
 */
export const getConsumoMPPlanReal = async (params) => {
  const { api } = await import("./api_backend");
  const response = await api.get("analitica/consumo-mp/plan-real/", { params });
  return response.data;
};
 
/**
 * Top 5 TPUs por kg consumidos en el rango de semanas.
 * Fuente: ConsumoMateriaPrima + ConsumoMateriaPrimaTPU (formularios en BD).
 *
 * @param {object} params - { semana_inicio, semana_fin, anio? }
 * @returns {{ top_tpu: [{tpu, cantidad_kg}], total_kg }}
 */
export const getConsumoMPTPU = async (params) => {
  const { api } = await import("./api_backend");
  const response = await api.get("analitica/consumo-mp/tpu/", { params });
  return response.data;
};